import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@/components/homepage/Button';
import Input from '@/components/Form/Input';
import useStore from '@/store/useStore';
import beTwMerge from '@/utils/beTwMerge';
import { sha256 } from '@/utils/utils';
import { MANUAL_SIGN_IN, BE_LOG_IN } from '@/consts/GA';
import useMD5HashEmail from '@/hooks/useMD5HashEmail';
import { getServiceName } from '@/utils/showroom';
import useFormPersist from '@/components/AppointmentBookingModal/useFormPersist';
import { klaviyoIdentify } from '@/services/forms.service';

// Component for Confirm Page
export default function PageFour({
  info,
  setInfo,
  confirmDateError,
  setConfirmDateError,
}: {
  info: any;
  setInfo: any;
  confirmDateError: boolean;
  setConfirmDateError: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const isMobile = useStore(state => state.isMobile);
  const { isAuthenticated } = useStore(state => state.user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputs = isAuthenticated
    ? [
        {
          id: 'firstName',
          name: 'firstName',
          label: 'First Name',
          labelClassName: 'tw-text-sm',
          maxLength: 32,
          fullWidth: true,
          type: 'text',
          registerObject: {
            required: true,
            validate: {
              isValid: v => /^(?!\s*$).+/.test(v.trim()) || 'First Name is required.',
            },
          },
        },
        {
          id: 'lastName',
          name: 'lastName',
          label: 'Last Name',
          labelClassName: 'tw-text-sm',
          maxLength: 32,
          fullWidth: true,
          type: 'text',
          registerObject: {
            required: true,
            validate: {
              isValid: v => /^(?!\s*$).+/.test(v.trim()) || 'Last Name is required.',
            },
          },
        },
        {
          id: 'email',
          name: 'email',
          label: 'Email Address',
          labelClassName: 'tw-text-sm',
          maxLength: 75,
          fullWidth: true,
          type: 'email',
          registerObject: {
            required: true,
            validate: {
              isValid: v => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/.test(v) || 'Invalid Email Address.',
            },
          },
        },
        {
          id: 'phone',
          name: 'phone',
          label: 'Phone',
          labelClassName: 'tw-text-sm',
          fullWidth: true,
          type: 'number',
          registerObject: {
            required: true,
            validate: {
              isValid: v =>
                /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(v.trim()) || 'Invalid Phone Number.',
            },
          },
        },
        {
          id: 'appointmentAlerts',
          name: 'appointmentAlerts',
          label:
            'Text me appointment alerts. Message and data rates may apply.<br /><small className="tw-text-[10px] tw-mt-1">*By checking this box, you consent to periodic updates (incl. automated) about your appointment, order, and experience (incl. surveys). Consent not a condition of purchase.</small>',
          labelClassName: '',
          fullWidth: true,
          type: 'checkbox',
          registerObject: {
            required: false,
          },
        },
      ]
    : [
        {
          id: 'firstName',
          name: 'firstName',
          label: 'First Name',
          labelClassName: 'tw-text-sm',
          maxLength: 32,
          fullWidth: true,
          type: 'text',
          registerObject: {
            required: true,
            validate: {
              isValid: v => /^(?!\s*$).+/.test(v.trim()) || 'First Name is required.',
            },
          },
        },
        {
          id: 'lastName',
          name: 'lastName',
          label: 'Last Name',
          labelClassName: 'tw-text-sm',
          maxLength: 32,
          fullWidth: true,
          type: 'text',
          registerObject: {
            required: true,
            validate: {
              isValid: v => /^(?!\s*$).+/.test(v.trim()) || 'Last Name is required.',
            },
          },
        },
        {
          id: 'email',
          name: 'email',
          label: 'Email Address',
          labelClassName: 'tw-text-sm',
          maxLength: 75,
          fullWidth: true,
          type: 'email',
          registerObject: {
            required: true,
            validate: {
              isValid: v => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/.test(v) || 'Invalid Email Address.',
            },
          },
        },
        {
          id: 'phone',
          name: 'phone',
          label: 'Phone',
          maxLength: 32,
          labelClassName: 'tw-text-sm',
          fullWidth: true,
          type: 'number',
          registerObject: {
            required: true,
            validate: {
              isValid: v =>
                /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(v.trim()) || 'Invalid Phone Number.',
            },
          },
        },
        {
          id: 'optionalPassword',
          name: 'optionalPassword',
          label: 'Enter password to sign in or create account (optional)',
          maxLength: 32,
          className: '!tw-border-color-grey-500',
          labelClassName: 'tw-text-sm tw-text-color-grey-500',
          fullWidth: true,
          type: 'password',
          registerObject: {
            required: false,
            validate: {
              isValid(value) {
                if (value.length > 0) {
                  return /(a^|^.{6,})$/.test(value) || 'Password must have at least 6 characters.';
                }

                return true;
              },
            },
          },
        },
        {
          id: 'appointmentAlerts',
          name: 'appointmentAlerts',
          label:
            'Text me appointment alerts. Message and data rates may apply.<br /><small className="tw-text-[10px] tw-mt-1">*By checking this box, you consent to periodic updates (incl. automated) about your appointment, order, and experience (incl. surveys). Consent not a condition of purchase.</small>',
          labelClassName: '',
          fullWidth: true,
          type: 'checkbox',
          registerObject: {
            required: false,
          },
        },
      ];
  const bookAppointment = useStore(state => state.bookAppointment);

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const {
    register,
    getValues,
    trigger,
    setFocus,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm();

  // Persist form data
  useFormPersist('confirm-page-form', {
    watch,
    setValue,
    exclude: ['optionalPassword'],
    storage: window.localStorage,
  });

  const { hashEmail } = useMD5HashEmail();

  // Function to submit form
  async function submitForm() {
    // Fails early if there is not a provider id
    if (info.setsterProviderId === null) {
      setConfirmDateError(true);
      return;
    }

    const isFormValid = await trigger();
    const { email, firstName, lastName, phone, optionalPassword, appointmentAlerts } = getValues() as any;
    const { track_event: trackGAEvent, dataLayer = {} } = window as any;

    const offset = info.selectedDate.getTimezoneOffset() || 0;
    const newDate = new Date(info.selectedDate.getTime() - offset * 60 * 1000);
    const date = newDate.toISOString().split('T')[0];
    const cf5 =
      localStorage.getItem('_bamAttributionPath') == null
        ? {}
        : [localStorage.getItem('_bamAttributionPath'), 'Attribution Path'];
    const cookies = document.cookie.split(';');
    const bamAttributionCookie = cookies.find(cookie => cookie.includes('_bamAttribution'));
    const wurflCookie = cookies.find(cookie => cookie.includes('wurfl'));
    const cf6 = bamAttributionCookie == null ? {} : [bamAttributionCookie, 'First Click'];
    const cf7 = wurflCookie == null ? {} : [wurflCookie, 'wurfl'];

    let time: string;
    const timeTrimSplit = info.selectedTime.trim().split(':');
    if (info.selectedTime.includes('PM') && `${timeTrimSplit[0][0]}${timeTrimSplit[0][1]}` !== '12') {
      time = `${parseInt(timeTrimSplit[0], 10) + 12}:${timeTrimSplit[1].slice(0, 2)}`;
    } else {
      time = `${timeTrimSplit[0]}:${timeTrimSplit[1].slice(0, 2)}`;
    }
    const datetime = `${date} ${time}`;

    const formData = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      start_date: `${datetime}`,
      start_tz:
        // eslint-disable-next-line no-nested-ternary
        info.selectedShowroomStore.modalName === 'Virtual Appointment'
          ? info.selectedTimezoneName !== ''
            ? info.selectedTimezoneName
            : 'America/Los_Angeles'
          : 'America/Los_Angeles',
      service_id: info.isSelectedDateNightTime ? info.selectedServiceDateNightId : info.selectedServiceId,
      is_mobile: isMobile,
      location_id: info.selectedShowroomStore.id,
      provider_id: info.setsterProviderId,
      is_embed: false,
      other_notes: '',
      custom_fields_5: cf5,
      custom_fields_6: cf6,
      custom_fields_7: cf7,
      receive_update: false,
      alert_reminders: appointmentAlerts,
    };

    if (isFormValid) {
      setIsSubmitting(true);

      try {
        setIsLoading(true);
        const { data } = await bookAppointment(
          optionalPassword ? { ...formData, password: optionalPassword } : formData
        );

        if (data.status === 'ok') {
          const hashedEmail = await hashEmail(email);
          klaviyoIdentify({
            email: formData.email,
            firstName: formData.first_name,
            lastName: formData.last_name,
          });
          (window as any).dataLayer.push({
            userEmail: formData.email,
            userHashedEmail: await sha256(formData.email),
            userPhoneNumber: formData.phone,
            userFirstName: formData.first_name,
            userLastName: formData.last_name,
            hashed_user_email_md5: hashedEmail,
            userID: data.results.user.id,
          });
          const timeSplit = info.selectedTime.split(' ');
          const startTime = `${timeSplit[0]} ${timeSplit[timeSplit.length - 1]}`;

          if (info.selectedShowroomStore.modalName === 'Virtual Appointment') {
            dataLayer.push({
              event: 'appointment_form',
              showroom_page_section: 'confirmation',
              showroom_action: 'book appointment',
              showroom_location: 'virtual',
              general_event_details: `${startTime}|${date}`,
            });
            dataLayer.push({ event: 'virtual_appointment_booked' });
            trackGAEvent('Scheduling', 'Virtual Appointment', 'Final Details|Virtual Appointment|Book Appointment');
            if (!data.results.create_account && data.results.user.id) {
              dataLayer.push({
                event: BE_LOG_IN,
                form_location: 'virtual appointment form',
                general_event_details: MANUAL_SIGN_IN,
              });
              dataLayer.push({
                event: 'appointment_form',
                showroom_page_section: 'confirmation',
                showroom_action: 'sign in',
                showroom_location: 'virtual',
                general_event_details: 'Undefined',
              });
            }
          } else {
            const showroomAction = `book appointment|${info.selectedServiceName}|${info.isSelectedDateNightTime ? 'Date Night Experience' : 'Standard Appointment'}`;
            dataLayer.push({
              event: 'appointment_form',
              showroom_page_section: 'confirmation',
              showroom_action: showroomAction,
              showroom_location: info.selectedShowroomStore.modalName,
              general_event_details: `${startTime}|${date}`,
            });
            dataLayer.push({
              event: 'setster_appointment_book',
            });

            trackGAEvent(
              'Scheduling',
              'Setster Appointment',
              `Final Details|${info.selectedShowroomStore.modalName}|Book Appointment`
            );
            if (!data.results.create_account && data.results.user.id) {
              dataLayer.push({
                event: BE_LOG_IN,
                form_location: 'setster appointment form',
                general_event_details: MANUAL_SIGN_IN,
              });
              dataLayer.push({
                event: 'appointment_form',
                showroom_page_section: 'confirmation',
                showroom_action: 'sign in',
                showroom_location: info.selectedShowroomStore.modalName,
                general_event_details: 'Undefined',
              });
            }
          }
          if (info.selectedServiceName === 'Pick Up' || info.selectedServiceName === 'Drop Off') {
            setInfo('formName', firstName);
            setInfo('activePage', 5);
            if (data.results.create_account && optionalPassword && !isAuthenticated) {
              dataLayer.push({
                event: 'appointment_form',
                showroom_page_section: 'confirmation',
                showroom_action: 'create account',
                showroom_location: info.selectedShowroomStore.modalName,
                general_event_details: 'Undefined',
              });
              dataLayer.push({
                event: 'sign_up',
                form_location: 'setster appointment form',
                general_event_details: MANUAL_SIGN_IN,
              });
              trackGAEvent(
                'Scheduling',
                'Setster Appointment',
                `Final Details|${info.selectedShowroomStore.modalName}|Create Account`
              );
            }
          } else {
            const isVirtual = info.selectedShowroomStore.modalName === 'Virtual Appointment';
            if (data.results.create_account && optionalPassword && !isAuthenticated) {
              dataLayer.push({
                event: 'appointment_form',
                showroom_page_section: 'confirmation',
                showroom_action: 'create account',
                showroom_location: isVirtual ? 'virtual' : info.selectedShowroomStore.modalName,
                general_event_details: 'Undefined',
              });
              dataLayer.push({
                event: 'sign_up',
                form_location: isVirtual ? 'virtual appointment form' : 'setster appointment form',
                general_event_details: MANUAL_SIGN_IN,
              });
              trackGAEvent(
                'Scheduling',
                isVirtual ? 'Virtual Appointment' : 'Setster Appointment',
                `Final Details|${isVirtual ? 'Virtual Appointment' : info.selectedShowroomStore.modalName}|Create Account`
              );
            }
            (window as Window).open(data.results.appointment_url, '_self');
          }
          setInfo('successfullBooking', true);
          localStorage.removeItem('confirm-page-form');
        } else if (data.status === 'failed') {
          setIsLoading(false);
          if (data?.errors?.password?.[0]) {
            setError('optionalPassword', { type: 'manual', message: data.errors['password'][0] });
          }

          if (data?.errors?.start_date?.[0] || data?.errors?.provider_id?.[0]) {
            setConfirmDateError(true);
          }

          setIsSubmitting(false);
        }
      } catch (error) {
        setIsLoading(false);
        setConfirmDateError(true);
        reset();
        setIsSubmitting(false);
      }
    }
  }

  const breakInputIndex = isAuthenticated ? 4 : 5;

  useEffect(() => {
    const firstError = Object.keys(errors).reduce<any>((field, a) => (errors[field] ? field : a), null);

    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);

  return (
    <div className="tw-pt-4 tw-px-4 tw-flex tw-flex-col tw-gap-y-0 lg:tw-max-w-[902px] lg:tw-mx-auto lg:tw-overflow-scroll tw-scrollbar-none lg:tw-h-full !tw-tracking-[0.7px] lg:!tw-tracking-[0.9px] tw-bg-transparent">
      <h3 className="tw-uppercase tw-px-4 tw-font-[500] tw-text-sm tw-text-center tw-m-0 lg:tw-text-lg tw-text-be-dark-gray tw-font-[Avenir]">
        Confirm details
      </h3>
      <div className="tw-grid tw-grid-cols-1 tw-pt-5 lg:tw-grid-cols-2 tw-gap-x-8 lg:tw-overflow-scroll tw-scrollbar-none lg:tw-h-full xl:tw-overflow-auto xl:tw-h-full">
        <form
          onSubmit={e => {
            e.preventDefault();
            submitForm();
          }}
        >
          <div className="tw-grid tw-gap-y-[21px]">
            {inputs.slice(0, breakInputIndex).map(input => (
              <Input
                {...input}
                key={input.name}
                register={register as any}
                name={input.name}
                id={input.id}
                error={errors[input.name] as any}
                className={beTwMerge(
                  '[&>label]:tw-text-sm [&>input]:tw-text-sm [&>input]:tw-pb-1 tw-relative [&>p]:tw-absolute [&>p]:tw-left-0[&>p]:tw-p-0 [&>p]:-tw-bottom-4 focus:[&>input]:!tw-border-[1.5px] focus:[&>input]:!tw-border-be-dark-gray',
                  input.name === 'optionalPassword' &&
                    !!errors['optionalPassword']?.message &&
                    '[&_p]:-tw-bottom-[36px]'
                )}
                labelClassName={input.id === 'optionalPassword' ? 'tw-bottom-2 tw-top-auto' : ''}
                errorColor="#C21419"
              />
            ))}
          </div>
          <div className={beTwMerge('tw-mt-5', errors?.['optionalPassword']?.message && 'tw-mt-11')}>
            <div className="tw-grid tw-gap-y-2 [&>div>div>label]:tw-mt-0 [&>div>div>div]:!tw-border-[#ababab]">
              {inputs.slice(breakInputIndex).map(input => {
                if (input.type === 'checkbox') {
                  const { appointmentAlerts } = JSON.parse(window?.localStorage?.getItem('confirm-page-form') || '{}');
                  const checkboxValue = !!appointmentAlerts;

                  return (
                    <Input
                      {...input}
                      key={input.name}
                      register={register as any}
                      name={input.name}
                      id={input.id}
                      customCheckboxValue={checkboxValue}
                      error={errors[input.name] as any}
                      className="[&>div>label>div>div]:!tw-font-[350] tw-font-book [&>div>label>div>div]:tw-tracking-[0px]"
                      errorColor="#C21419"
                    />
                  );
                }

                return (
                  <Input
                    {...input}
                    key={input.name}
                    register={register as any}
                    name={input.name}
                    id={input.id}
                    error={errors[input.name] as any}
                    className="[&>div>label>div>div]:!tw-font-[350] tw-font-book [&>div>label>div>div]:tw-tracking-[0px]"
                    errorColor="#C21419"
                  />
                );
              })}
            </div>
          </div>

          <div>
            <span className="tw-text-center tw-text-color-grey-500 tw-w-full tw-block tw-text-[10px] tw-mt-6 !tw-font-book !tw-font-[350] !tw-tracking-[0px]">
              By clicking Book Appointment, you agree to our{' '}
              <a
                className="tw-text-color-grey-500 tw-underline"
                target="_blank"
                href="/security-and-privacy-policies/#privacy"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a className="tw-text-color-grey-500 tw-underline" target="_blank" href="/terms-and-conditions/">
                Terms and Conditions
              </a>
              .
            </span>
            <Button
              disabled={isSubmitting}
              onClick={submitForm}
              label={isLoading ? 'Loading...' : 'Book Appointment'}
              type="primary"
              className="tw-mt-2 lg:tw-w-full lg:tw-max-w-none tw-max-w-none"
            />
          </div>
        </form>

        <div className="tw-hidden lg:tw-flex lg:tw-gap-y-6 tw-p-6 lg:tw-flex-col lg:tw-border-[#D9D9D9] lg:tw-border-solid lg:tw-border-[1.5px] tw-h-fit">
          <div>
            <p className="tw-text-xs tw-font-[400] tw-mb-1 tw-tracking-wider">LOCATION</p>
            <h4 className="tw-m-0 tw-text-sm tw-font-[500] tw-mt-1 tw-text-be-dark-gray">
              {info.selectedShowroomStore.modalName}
            </h4>
          </div>
          <div>
            <p className="tw-text-xs tw-font-[400] tw-mb-1 tw-tracking-wider">SERVICE</p>
            <h4 className="tw-m-0 tw-text-sm tw-font-[500] tw-mt-1 tw-text-be-dark-gray">
              {getServiceName(info.selectedServiceName, info.isSelectedDateNightTime)}
            </h4>
          </div>
          <div>
            <p
              className={beTwMerge(
                'tw-text-xs tw-font-[400] tw-mb-1 tw-tracking-wider',
                confirmDateError ? 'tw-text-[#C21419]' : ''
              )}
            >
              DATE & TIME
            </p>
            <h4
              className={beTwMerge(
                'tw-m-0 tw-text-sm tw-font-[500] tw-mt-1 tw-text-be-dark-gray',
                confirmDateError ? 'tw-text-[#C21419]' : ''
              )}
            >
              {days[new Date(info.selectedDate).getDay()]}, {months[new Date(info.selectedDate).getMonth()]}{' '}
              {new Date(info.selectedDate).getDate()} at {info.selectedTime}
            </h4>
            {confirmDateError && (
              <p className="tw-mt-2 tw-text-[11px] tw-text-[#C21419]">
                The appointment time you selected is no longer available. Please select another date & time.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
